import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Table, Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import {
  getStockTransfersApi,
  completeStockTransferApi,
  cancelStockTransferApi,
  getStockTransferDocumentApi,
  openUniformDocument,
  uniformErrorMessage,
} from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler, debounce } from 'utils/common';
import { getStatusBadge, transferStatusOptions } from '../../constants';
import PageHeader from 'components/PageHeader';
import ListShell from 'components/ListShell';
import TransferFormModal from './TransferFormModal';
import TransferDocumentActions from './TransferDocumentActions';
import TransferExpandedPanel, { hasTransferDetail } from './TransferExpandedPanel';
import useStickyIdentity from '../../components/useStickyIdentity';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

/** Narrow the in-flight document key to one record before handing it to that
 *  record's panel, so opening a document on one row leaves every other open
 *  panel's props untouched and `React.memo` can bail out of re-rendering them.
 *  The trailing colon makes the prefix test id-exact ("12:inline" is not a
 *  match for record 1). */
const scopedDocKey = (openingDoc, id) => (
  openingDoc && openingDoc.startsWith(`${id}:`) ? openingDoc : null
);

const TransferList = () => {
  // Hook form, not static Modal.confirm: the static call renders outside the
  // React tree and so never sees the app's ConfigProvider theme (index.jsx),
  // rendering unthemed.
  const [modal, modalContextHolder] = Modal.useModal();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  // `"<transferId>:<disposition>"` while a signed URL is being fetched, so the
  // spinner lands on the button that was pressed rather than on both.
  const [openingDoc, setOpeningDoc] = useState(null);

  const [referenceInput, setReferenceInput] = useState('');
  const [reference, setReference] = useState('');

  // "Latest request wins" guard, covering the loading reset too, so a slow
  // response for a filter combination the user has since changed away from
  // (rapid status-tab or search-box edits) can't land after a newer one and
  // repaint the table with stale rows or leave `loading` stuck on.
  const reqIdRef = useRef(0);

  const debouncedSetReference = useMemo(() => debounce((value) => {
    setReference(value);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  }, 400), []);

  const handleReferenceChange = (e) => {
    const value = e.target.value;
    setReferenceInput(value);
    debouncedSetReference(value);
  };

  const fetchData = useCallback(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    const params = {
      page: pageSettings.page,
      page_size: pageSettings.pageSize,
    };
    if (statusFilter) params.status = statusFilter;
    if (reference) params.reference = reference;
    getStockTransfersApi(params).then((response) => {
      if (reqId !== reqIdRef.current) return;
      setData(response.data?.results || []);
      setPageSettings((prev) => ({ ...prev, total: response.data?.count || 0 }));
    }).catch((e) => {
      if (reqId !== reqIdRef.current) return;
      toast.error(uniformErrorMessage(e, 'Failed to load stock transfers'), toastOpts);
    }).finally(() => {
      if (reqId === reqIdRef.current) setLoading(false);
    });
  }, [pageSettings.page, pageSettings.pageSize, statusFilter, reference]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const handleComplete = (id) => {
    setActionLoading(id);
    completeStockTransferApi(id).then(() => {
      toast.success('Transfer completed', toastOpts);
      setActionLoading(null);
      fetchData();
    }).catch((e) => {
      setActionLoading(null);
      toast.error(uniformErrorMessage(e, 'Failed to complete transfer'), toastOpts);
    });
  };

  const handleCancel = (id) => {
    modal.confirm({
      title: 'Cancel Transfer',
      centered: true,
      content: 'Are you sure? Stock will be restored to the source warehouse.',
      okText: 'Yes, Cancel',
      okType: 'danger',
      onOk: () => {
        setActionLoading(id);
        cancelStockTransferApi(id).then(() => {
          toast.success('Transfer cancelled, stock restored', toastOpts);
          setActionLoading(null);
          fetchData();
        }).catch((e) => {
          setActionLoading(null);
          toast.error(uniformErrorMessage(e, 'Failed to cancel transfer'), toastOpts);
        });
      }
    });
  };

  /** View (`inline`) and Download (`attachment`) are the same request with a
   *  different disposition; the backend stamps it onto the presigned URL.
   *  Stable identity (no deps) so the memoised expanded panels don't re-render
   *  on every parent render. */
  const handleOpenDocument = useCallback((record, disposition) => {
    const key = `${record.id}:${disposition}`;
    setOpeningDoc(key);
    openUniformDocument((p) => getStockTransferDocumentApi(record.id, p), disposition)
      .catch((e) => toast.error(uniformErrorMessage(
        e,
        disposition === 'attachment' ? 'Failed to download PO document' : 'Failed to open PO document',
      ), toastOpts))
      // Clear only our own key: a second action started meanwhile owns the
      // spinner now, and blanking it would strand that button unlit.
      .finally(() => setOpeningDoc((current) => (current === key ? null : current)));
  }, []);

  const pinLeft = useStickyIdentity();

  const columns = [
    {
      // Pinned. The row already scrolls on a laptop at ten columns, and
      // the reference is the only cell that says which transfer this is.
      // The expand icon antd inserts to the left inherits `fixed` from
      // this column, so the toggle stays reachable too.
      title: 'Reference',
      dataIndex: 'reference', ...pinLeft(170),
      // No Notes column: this grid already runs to ten columns and scrolls
      // horizontally on a laptop, and notes are free text that truncates to
      // nothing at column width. The notes live in the expanded panel at full
      // width; this glyph is the at-a-glance signal that a row has any, so a
      // reader knows which rows are worth opening.
      render: (value, record) => (
        <span className="d-inline-flex align-items-center gap-2">
          <span>{value || '—'}</span>
          {record.notes?.trim() ? (
            <>
              <i className="fa fa-sticky-note" aria-hidden="true"
                title="This transfer has notes — expand the row to read them"
                style={{ color: 'var(--brand-text-muted)', fontSize: 11 }} />
              <span className="visually-hidden">Has notes. Expand the row to read them.</span>
            </>
          ) : null}
        </span>
      ),
    },
    { title: 'Source', dataIndex: 'source_warehouse_name' },
    { title: 'Destination', dataIndex: 'destination_warehouse_name' },
    {
      title: 'Items',
      render: (_, record) => record.line_count ?? (record.uniform_item_name ? 1 : 0)
    },
    {
      title: 'Total Qty',
      render: (_, record) => record.total_quantity ?? record.quantity ?? 0
    },
    {
      title: 'PO',
      render: (_, record) => (
        <div className="d-flex align-items-center gap-2">
          <span>{record.po_number || '—'}</span>
          <TransferDocumentActions
            record={record}
            openingDoc={scopedDocKey(openingDoc, record.id)}
            onOpen={handleOpenDocument}
            compact
          />
        </div>
      )
    },
    { title: 'Date', dataIndex: 'transfer_date', render: (d) => formatDateHandler(d) },
    { title: 'Initiated By', dataIndex: 'initiated_by_name' },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <span style={getStatusBadge(status)}>{status}</span>
    },
    {
      title: 'Action',
      render: (_, record) => {
        if (record.status !== 'PENDING') return null;
        return (
          <div className="d-flex gap-2">
            <Button label="Complete" className="btn btn-sm btn-success"
              loading={actionLoading === record.id} onClick={() => handleComplete(record.id)} />
            <Button label="Cancel" className="btn btn-sm btn-outline-danger"
              loading={actionLoading === record.id} onClick={() => handleCancel(record.id)} />
          </div>
        );
      }
    }
  ];

  const filterItems = [
    { key: '', label: 'All' },
    ...transferStatusOptions.map((o) => ({ key: o.value, label: o.label })),
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {modalContextHolder}
        <PageHeader
          title="Stock Transfers"
          actions={
            <Button label="New Transfer" icon="fa fa-plus" className="btn btn-primary btn-sm"
              onClick={() => setShowCreateModal(true)} />
          }
        >
          <input className="form-control" style={{ width: 240 }} placeholder="Search reference..."
            aria-label="Search stock transfers by reference"
            value={referenceInput} onChange={handleReferenceChange} />
        </PageHeader>

        <ListShell
          filters={filterItems}
          activeKey={statusFilter}
          onFilterChange={(key) => { setStatusFilter(key); setPageSettings(prev => ({ ...prev, page: 1 })); }}
          resultCount={pageSettings.total}
          loading={loading}
        >
          <Table
            columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
            expandable={{
              expandedRowRender: (record) => (
                <TransferExpandedPanel
                  record={record}
                  openingDoc={scopedDocKey(openingDoc, record.id)}
                  onOpenDocument={handleOpenDocument}
                />
              ),
              // Notes and the PO reference are only visible here, so a transfer
              // that carries either still has to be openable even if its lines
              // somehow came back empty.
              rowExpandable: hasTransferDetail,
            }}
            pagination={{
              total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
              pageSizeOptions, itemRender, showSizeChanger: true
            }}
            onChange={handleChange} scroll={{ x: "max-content" }}
          />
        </ListShell>

        {showCreateModal && (
          <TransferFormModal
            open={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onCreated={() => { setShowCreateModal(false); fetchData(); }}
          />
        )}
      </div>
    </div>
  );
};

export default TransferList;
